import React, { useState, useRef, useEffect } from 'react';
import { InputSearchProps } from '@/types/components';

export const InputSearch: React.FC<InputSearchProps> = ({
  placeholder = 'Search...',
  value: controlledValue,
  onChange,
  onSearch,
  onClear,
  icon = 'ri-search-line',
  size = 'medium',
  disabled = false,
  loading = false,
  debounceMs = 500,
  showClearButton = true,
  suggestions = [],
  onSuggestionClick,
  className = '',
}) => {
  const [internalValue, setInternalValue] = useState(controlledValue || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const sizeClasses = {
    small: 'h-8 text-sm',
    medium: 'h-10 text-sm',
    large: 'h-12 text-base',
  };

  const iconSizes = {
    small: 'text-[14px]',
    medium: 'text-[16px]',
    large: 'text-[18px]',
  };

  const paddingClasses = {
    small: 'pl-8 pr-8',
    medium: 'pl-10 pr-10',
    large: 'pl-12 pr-12',
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isControlled) setInternalValue(newValue);
    onChange?.(newValue);

    if (onSearch && debounceMs > 0) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onSearch(newValue), debounceMs);
    } else if (onSearch) {
      onSearch(newValue);
    }

    if (suggestions.length > 0 && newValue.length > 0) {
      setShowSuggestions(true);
      setFocusedIndex(-1);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleClear = () => {
    const newValue = '';
    if (!isControlled) setInternalValue(newValue);
    onChange?.(newValue);
    onClear?.();
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!isControlled) setInternalValue(suggestion);
    onChange?.(suggestion);
    onSuggestionClick?.(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) handleSuggestionClick(suggestions[focusedIndex]);
        break;
      case 'Escape':
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const filteredSuggestions = suggestions.filter((s) =>
    s.toLowerCase().includes(currentValue.toLowerCase())
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <i
          className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-icon-soft-400 ${iconSizes[size]} pointer-events-none`}
        ></i>

        <input
          ref={inputRef}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (filteredSuggestions.length > 0 && currentValue.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full ${sizeClasses[size]} ${paddingClasses[size]}
            border border-stroke-sub-300 rounded-md bg-bg-white-0
            text-text-strong-950 placeholder:text-text-soft-400
            focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary
            disabled:bg-bg-weak-50 disabled:text-text-sub-600 disabled:cursor-not-allowed
            ${loading ? 'animate-pulse' : ''}
          `.trim()}
        />

        {showClearButton && currentValue.length > 0 && !loading && (
          <button
            onClick={handleClear}
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-icon-soft-400 hover:text-text-sub-600 ${iconSizes[size]}`}
          >
            <i className="ri-close-line"></i>
          </button>
        )}
      </div>

      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-bg-white-0 border border-stroke-soft-200 rounded-md shadow-card-sm max-h-60 overflow-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`
                w-full px-4 py-2 text-left text-sm text-text-strong-950 hover:bg-bg-weak-50 focus:bg-bg-weak-50 focus:outline-hidden
                ${index === focusedIndex ? 'bg-bg-weak-50' : ''}
              `.trim()}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InputSearch;
